import prismic from '@prismicio/client';
import { cast, validate } from './schema';

const cache = {};

export const loadPageData = async (page, getPath = ({ path }) => path, getId = ({ id }) => id) => {
  const route = page.$route || {};
  const path = getPath(route);
  const id = getId(route);
  const pageId = path + id;
  const schema = validate(page.schema || {});
  const data = cache[pageId] || await fetch(path, id);
  const resultData = unflatten(cast(data, schema));

  cache[pageId] = data;

  Object.assign(page, resultData);

  return resultData;
};
/**
 * Fetch single document by type (also called API ID)
 *
 * @param {String} path
 * @param {String | Function} id
 * @return {Promise<Object>}
 * @public
 */

export async function fetch(path, id) {
  const api = await prismic.getApi(process.env.NUXT_ENV_PRISMIC_PAGES_ENDPOINT);
  const uid = getUid(path);

  const doc = id
    ? await api.getByUID(uid, typeof id === 'function' ? id() : id)
    : await api.getSingle(uid);

  return doc ? doc.data : {};
}

/**
 * Unflatten prismic object.
 *
 * Examples:
 *
 *   unflatten({hero_title: 'hello'})
 *   // => { hero: { title: 'hello' }}
 *
 * @param {Object} obj
 * @return {Object}
 * @private
 */

export function unflatten(obj) {
  const result = {};
  Object.keys(obj).map((key) => tree(result, key, obj[key]));
  return result;
}

/**
 * Create uid from page path/name.
 *
 * @param {String} path
 * @return {String}
 * @private
 */

function getUid(path) {
  const names = sanitize(path).split('/');
  names.splice(0, 1, 'root');
  return names.join('_');
}

/**
 * Vue require context can add '/' at the end of a path and we want to make sure that:
 *  - a path always has a '/' at the begining and
 *  - a path does not end with a '/'
 *
 * Examples:
 *
 *   sanitize('hello')
 *   // => /hello
 *   sanitize('/hello')
 *   // => /hello
 *   sanitize('/hello/')
 *   // => /hello
 *   sanitize(':hello')
 *   // => /hello
 *
 * @param {String} path
 * @return {String}
 * @private
 */

function sanitize(path) {
  let sanitizedPath = path;
  if (sanitizedPath[0] !== '/') sanitizedPath = `/${sanitizedPath}`;
  if (sanitizedPath[path.length - 1] === '/') sanitizedPath = sanitizedPath.slice(0, -1);
  return sanitizedPath.replace(/:/g, '');
}

/**
 * Transform a snake case property into a tree of properties.
 *
 * Examples:
 *
 *   tree({}, 'hello_world', 'super')
 *   // => { hello: { world: 'super' }}
 *
 * @private
 */

function tree(obj, prop, value) {
  let tmp = obj;
  const names = prop.split('_');
  names.forEach((key, idx) => {
    tmp[key] = tmp[key] || (idx === (names.length - 1) ? value : {});
    tmp = tmp[key];
  });
}
